import * as React from 'react';
import { get } from 'lodash';
import {
  compose,
  withHandlers,
  mapProps,
  withStateHandlers,
  lifecycle,
  branch,
  renderComponent,
  withState
} from 'recompose';
import { reduxForm, FormSection } from 'redux-form';
import { Row, Col, Grid } from 'react-flexbox-grid';

import Button from '@inqool/uas-web-core/es/components/controls/Button';

import {
  InputField,
  Input,
  Date,
  DefaultField,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  Toggle,
  ToggleField
} from '@components/form';
import { connect } from 'react-redux';
import { withNotifications } from '@components/hoc/withNotifications';
import { required, requiredWithText, maxLength } from '@utils/validators';
import { saveSoDelict, getDelict } from '@modules/delicts/_delictsActions';
import { Loader } from '@components/loader';

const requiredISZR = requiredWithText('Pachatele je potřeba vylustrovat v ISZR');
const maxLength3 = maxLength(3);

const enhance = compose<any, any>(
  withNotifications({ saveSoDelict, getDelict }),
  connect(state => ({
    delict: get(state, 'form.module-events-delicts.values')
  })),
  withState<{}, boolean, string, string>('canSubmit', 'handleCanSubmit', true),
  withStateHandlers<any, any>(({ delict }) => ({ delict }), { setDelict: state => delict => ({ ...state, delict }) }),
  lifecycle<any, any, any>({
    async componentDidMount() {
      const { delict, setDelict, delictId, getDelictNotify } = this.props;
      if (!delict) {
        const newDelict = await getDelictNotify(delictId);
        setDelict(newDelict);
      }
    }
  }),
  branch(({ delict }) => !delict, renderComponent(Loader)),
  withHandlers({
    onSubmit: ({ reloadDelict, delictId, closeItem, saveSoDelictNotify, canSubmit, handleCanSubmit }) => async ({
      delictParagraph
    }) => {
      if (canSubmit) {
        handleCanSubmit(false);
        const result = await saveSoDelictNotify({ delictId, body: { delictParagraph } });
        if (result) {
          await reloadDelict();
          closeItem();
          handleCanSubmit(true);
        }
      }
    }
  }),
  mapProps(({ delict, ...rest }) => ({
    initialValues: {
      date: get(delict, 'date'),
      type: get(delict, 'type'),
      intention: get(delict, 'intention'),
      paid: get(delict, 'paid'),

      officer: get(delict, 'officer'),
      offender: get(delict, 'offender'),
      fine: get(delict, 'fine'),

      delictParagraph: get(delict, 'type.paragraph')
    },
    isCzechCitizen: get(delict, 'offender.person.citizenship.n3') === '203',
    ...rest
  })),
  reduxForm({ form: 'module-events-delicts-isep' })
);

const View: React.SFC<any> = ({ handleSubmit, closeItem, isCzechCitizen }) => (
  <div style={{ overflow: 'auto', maxHeight: '70vh', minWidth: '50vw' }}>
    <form className="layout" onSubmit={handleSubmit}>
      <Grid fluid={true}>
        <Row>
          <Col md={12}>
            <Row>
              <Col md={12}>
                <h4>Zápis ISEP</h4>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <DefaultField
                  component={Date}
                  name={'date'}
                  label={'Datum a čas přestupku *'}
                  disabled={true}
                  validate={[required]}
                />
              </Col>
              {[{ name: 'intention', label: 'Úmysl' }, { name: 'paid', label: 'Zaplaceno' }].map(({ label, name }) => (
                <Col key={name} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <ToggleField component={Toggle} name={name} label={label} disabled={true} labelPosition="right" />
                </Col>
              ))}
              <Col md={12}>
                <RemoteAutoCompleteField
                  component={RemoteAutoComplete}
                  name={'type'}
                  label={'Typ přestupku *'}
                  disabled={true}
                  validate={[required]}
                  url={'/metropolis/api/ed/delict/types'}
                  labelFunction={o => get(o, 'fullName')}
                />
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <h4>Přestupce</h4>
              </Col>
            </Row>
            <FormSection name="offender.person">
              <Row>
                {[
                  { name: 'firstName', label: 'Jméno *' },
                  { name: 'lastName', label: 'Příjmení *' },
                  { name: 'birthDate', label: 'Datum narození *', component: Date },
                  {
                    name: 'aifo',
                    label: 'Identifikační číslo ISZR',
                    md: 12,
                    validate: isCzechCitizen ? [] : [requiredISZR]
                  }
                ].map(({ label, name, md = 4, component = Input, validate = [required] }) => (
                  <Col md={md} key={name}>
                    <DefaultField component={component} name={name} label={label} disabled={true} validate={validate} />
                  </Col>
                ))}
              </Row>
            </FormSection>

            <Row>
              <Col md={12}>
                <h4>Řešitel přestupku</h4>
              </Col>
            </Row>
            <FormSection name="officer">
              <Row>
                {[
                  { name: 'firstName', label: 'Jméno *' },
                  { name: 'lastName', label: 'Příjmení *' },
                  { name: 'officerNumber', label: 'Služební číslo *' }
                ].map(({ label, name }) => (
                  <Col md={4} key={name}>
                    <InputField component={Input} name={name} label={label} disabled={true} validate={[required]} />
                  </Col>
                ))}
              </Row>
            </FormSection>

            <Row>
              <Col md={12}>
                <h4>Doklad</h4>
              </Col>
            </Row>
            <FormSection name="fine">
              <Row>
                {[{ name: 'number', label: 'Evidenční číslo *' }, { name: 'value', label: 'Výše pokuty *' }].map(
                  ({ label, name }) => (
                    <Col md={4} key={name}>
                      <InputField component={Input} name={name} label={label} disabled={true} validate={[required]} />
                    </Col>
                  )
                )}
                <Col md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <ToggleField
                    component={Toggle}
                    name={'paid'}
                    label={'Zaplaceno'}
                    disabled={true}
                    labelPosition="right"
                  />
                </Col>
              </Row>
            </FormSection>

            <Row>
              <Col md={12}>
                <h4>Paragraf</h4>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <InputField
                  component={Input}
                  name={'delictParagraph'}
                  label={'Paragraf *'}
                  validate={[required, maxLength3]}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Button label="Zapsat ISEP" primary={true} type={'submit'} style={{ margin: '8px' }} />
        <Button label="Zrušit" style={{ margin: '8px' }} onClick={closeItem} />
      </Grid>
    </form>
  </div>
);

export const EventsFormDelictsSaveISEPForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormDelictsSaveISEPForm.tsx